import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { Identifier } from 'react-admin'
import { getVGSDomain } from 'shared/config/config'

import { getEquipmentResourceName } from '../helpers/utils'
import { getRequest, postRequest } from './common'

export function clearUserShippingAddress(id: Identifier) {
  let url = `/api/v1/console/users/${id}/clear-shipping-address/`
  return postRequest(url)
}

export function updateUserShippingAddress(id: Identifier, payload: any) {
  let url = `/api/v1/console/users/${id}/update-shipping-address/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function changeTeamsBundleSubscription(id: Identifier | undefined, payload: any) {
  let url = `/api/v1/console/teams/${id}/change-bundle/`
  return postRequest(url, payload)
}

export function clearTeamBillingAddress(id: Identifier) {
  let url = `/api/v1/console/teams/${id}/clear-billing-address/`
  return postRequest(url)
}

export function updateTeamBillingAddress(id: Identifier, payload: any) {
  let url = `/api/v1/console/teams/${id}/update-billing-address/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function clearLicense(id: Identifier) {
  let url = `/api/v1/console/users/${id}/clear-license/`
  return postRequest(url)
}

export function changeDriverStatus(
  id: Identifier,
  transitionAction: any,
  revokeAccessReason: string | null,
  approveFleet: boolean | null,
  deactivationDetail: string,
  carrierMCNumber: string | null
) {
  let url = `/api/v1/console/users/${id}/transition/`
  let payload: any = { transition: transitionAction }
  if (revokeAccessReason && revokeAccessReason.trim() !== '') {
    payload.reason = revokeAccessReason
  }
  if (approveFleet !== null) {
    payload.approve_fleet = approveFleet
  }
  if (deactivationDetail && deactivationDetail.trim() !== '') {
    payload.status_detail = deactivationDetail.trim()
  }
  if (carrierMCNumber && carrierMCNumber.trim() !== '') {
    payload.carrier_mc_number = carrierMCNumber
  }
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function addEquipment(teamId: Identifier, payload: any) {
  let url = `/api/v1/console/teams/${teamId}/add-equipment/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.CREATED) {
      return response.data
    }
  })
}

export function updateEquipment(equipmentId: Identifier, equipmentType: string, payload: any) {
  let url = `/api/v1/console/${equipmentType}s/${equipmentId}/`
  return axios.put(url, payload).then((response) => {
    return response.data
  })
}

export function addEquipmentInspectionReport(equipmentType: string, payload: any) {
  let url = `/api/v1/console/${equipmentType}-inspection-report/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.CREATED) {
      return response.data
    }
  })
}

export function updateEquipmentInspectionReport(
  inspectionReportId: Identifier,
  equipmentType: string,
  payload: any
) {
  let url = `/api/v1/console/${equipmentType}-inspection-report/${inspectionReportId}/`
  return axios.put(url, payload).then((response) => {
    return response.data
  })
}

export function removeEquipment(id: Identifier, statusReason: string, equipmentType: any) {
  let resourceName = getEquipmentResourceName(equipmentType)
  let url = `/api/v1/console/${resourceName}/${id}/remove/`
  let payload = {
    status_reason: statusReason,
  }
  return postRequest(url, payload)
}

export function unRemoveEquipment(id: Identifier, equipmentType: any) {
  let resourceName = getEquipmentResourceName(equipmentType)
  let url = `/api/v1/console/${resourceName}/${id}/unremove/`
  return postRequest(url)
}

export function updateInsurancePeriod(id: Identifier, payload: any, equipmentType: string) {
  let resourceName = getEquipmentResourceName(equipmentType)
  let url = `/api/v1/console/${resourceName}/${id}/insurance-period/`
  return postRequest(url, payload)
}

export function deleteInsurancePeriod(id: Identifier, payload: any, equipmentType: string) {
  let resourceName = getEquipmentResourceName(equipmentType)
  let url = `/api/v1/console/${resourceName}/${id}/delete-insurance-period/`
  return postRequest(url, payload)
}

export function getVinInfo(vinNumber: string, equipmentType: string) {
  let resourceName = getEquipmentResourceName(equipmentType)
  let url = `/api/v1/console/${resourceName}/vin-info/?vin_number=${vinNumber}`
  return getRequest(url)
}

export function uploadTeamDocumentation(id: Identifier, formData: any) {
  let url = `/api/v1/console/teams/${id}/documentation/`
  return axios
    .post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((response) => {
      if (response.status === HttpStatus.NO_CONTENT) {
        return response
      }
    })
}

export function offboardTeam(id: Identifier) {
  const url = `/api/v1/console/teams/${id}/offboard/`

  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function reapplyTeam(id: Identifier) {
  const url = `/api/v1/console/teams/${id}/reapply/`

  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function reonboardTeam(id: Identifier) {
  const url = `/api/v1/console/teams/${id}/reonboard/`

  return axios.post(url).then((response) => {
    if (response.status === HttpStatus.NO_CONTENT) {
      return
    }
  })
}

export function getUserChecklist(userId: Identifier) {
  let url = `/api/v1/console/users/${userId}/checklist/`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function getDocuments(userId: Identifier, folderName: string) {
  let url = `/api/v1/console/documents/${userId}/all/`
  return axios.get(url, { params: { folder_name: folderName } }).then((response) => {
    return response.data
  })
}

// @ts-ignore
export function createDocument(documentId: Identifier, payload: any, is_encrypted: boolean = false) {
  let url = `${is_encrypted ? getVGSDomain() : ''}/api/v1/console/documents/`
  let data = payload
  if (is_encrypted) {
    // convert to json payload
    data = {}
    payload.forEach((value: any, key: any) => (data[key] = value))
  }

  let headers = is_encrypted ? { withCredentials: true } : { 'Content-Type': 'multipart/form-data' }

  return axios.post(url, data, headers).then((response) => {
    return response.data
  })
}

export function updateDocument(documentId: Identifier, payload: any, is_encrypted: boolean = false) {
  let url = `${is_encrypted ? getVGSDomain() : ''}/api/v1/console/documents/${documentId}/`
  let data = payload
  if (is_encrypted) {
    // convert to json payload
    data = {}
    payload.forEach((value: any, key: any) => (data[key] = value))
  }

  let headers = is_encrypted ? { withCredentials: true } : { 'Content-Type': 'multipart/form-data' }

  return axios.patch(url, data, headers).then((response) => {
    return response.data
  })
}

export function deleteDocument(documentId: Identifier) {
  let url = `/api/v1/console/documents/${documentId}/`

  return axios.delete(url).then((response) => {
    return response.data
  })
}

export function getAllGlobalDocuments() {
  const url = '/api/v1/console/documents/all-global/'

  return axios.get(url).then((response) => {
    return response.data
  })
}

export function reviewDocument(documentId: Identifier, payload: any) {
  const url = `/api/v1/console/documents/${documentId}/review/`

  return axios.post(url, payload, { headers: { 'Content-Type': 'multipart/form-data' } }).then((response) => {
    return response.data
  })
}

export function getSettings() {
  let url = `/api/v1/console/settings/`
  return axios.get(url).then((response) => {
    return response.data
  })
}

export function applyReferralCode(id: Identifier, referralCodeId: Identifier) {
  const url = `/api/v1/console/users/${id}/apply-referral-code/`
  const payload = { referral_code_id: referralCodeId }
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function getTeamMembersELDAccounts(id: Identifier) {
  const url = `/api/v1/console/teams/${id}/members-eld-accounts/`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function partialUpdateELDAccount(id: Identifier, payload: any) {
  const url = `/api/v1/console/eld-accounts/${id}/`
  return axios.patch(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function confirmSignLeaseAgreementForTeam(id: Identifier) {
  let url = `/api/v1/console/teams/${id}/sign-lease-agreement/`
  return postRequest(url)
}

export function confirmMedicalCardForUser(id: Identifier) {
  let url = `/api/v1/console/users/${id}/confirm-medical-card/`
  return postRequest(url)
}

export function fileUccStatus(id: Identifier) {
  let url = `/api/v1/console/teams/${id}/file-ucc-status/`
  return postRequest(url)
}

export function confirmFleetApplication(id: Identifier) {
  let url = `/api/v1/console/teams/${id}/confirm-fleet-application/`
  return postRequest(url)
}

export function confirmOccupationalAccidentInsurance(id: Identifier) {
  let url = `/api/v1/console/users/${id}/confirm-insurance-occupational-accident/`
  return postRequest(url)
}

export function confirmOnboaardingCall(id: Identifier) {
  let url = `/api/v1/console/users/${id}/confirm-safety-call/`
  return postRequest(url)
}

export function confirmTruck(id: Identifier) {
  let url = `/api/v1/console/trucks/${id}/confirm/`
  return postRequest(url)
}

export function confirmTrailer(id: Identifier) {
  let url = `/api/v1/console/trailers/${id}/confirm/`
  return postRequest(url)
}

export function deleteConnectedIntegration(id: Identifier) {
  let url = `/api/v1/console/connected-integrations/${id}/`
  return axios.delete(url).then((response) => {
    if (response.status === HttpStatus.NO_CONTENT) {
      return response.data
    }

    throw response
  })
}

export async function addStaffUsers(emails: any, groups: any) {
  const url = '/api/v1/console/users/create-staff-user/'
  const response = await axios.post(url, { emails, groups })
  return response.status === HttpStatus.CREATED
}

export function syncOnboardingBackgroundCheck(userId: Identifier, initial: boolean) {
  let url = `/api/v1/console/users/${userId}/sync-background-check/${initial ? '?initial=true' : ''}`
  return axios.post(url, { params: { initial } }).then((response) => {
    return response.data
  })
}

export function checkOnboardingBackgroundCheck(userId: Identifier, initial: boolean) {
  let url = `/api/v1/console/users/${userId}/onboarding-background-check/`
  return axios.get(url, { params: { initial } }).then((response) => {
    return response.data
  })
}

export function orderOnboardingBackgroundCheck(userId: Identifier, initial: boolean) {
  let url = `/api/v1/console/users/${userId}/onboarding-background-check/${initial ? '?initial=true' : ''}`
  return axios.post(url, { params: { initial } }).then((response) => {
    return response.data
  })
}

export function updateTeamUserGroups(userId: Identifier, payload: any) {
  let url = `/api/v1/console/users/${userId}/update-team-user-groups/`
  return axios.post(url, payload).then((response) => {
    if (response.status !== HttpStatus.NO_CONTENT) {
      throw response
    }
  })
}

export function updateTeamUserSubscriptionItems(userId: Identifier, payload: any) {
  let url = `/api/v1/console/users/${userId}/update-team-user-subscription-items/`
  return axios.post(url, payload).then((response) => {
    if (response.status !== HttpStatus.NO_CONTENT) {
      throw response
    }
  })
}

export function fetchUser(userId: Identifier) {
  return axios.get(`/api/v1/console/users/${userId}/`).then((response) => {
    return response.data
  })
}

export function moveUserTeam(userId: Identifier, team: any) {
  const payload = { team: team }
  return axios.post(`/api/v1/console/users/${userId}/move-team/`, payload).then((response) => {
    return response.data
  })
}

export function updateTeamLegalBusinessName(teamId: Identifier, payload: { legal_business_name: string }) {
  let url = `/api/v1/console/teams/${teamId}/update-legal-business-name/`
  return axios.post(url, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export function resetUser(userId: Identifier) {
  return axios.post(`/api/v1/console/users/${userId}/reset/`).then((response) => {
    return response.data
  })
}

export function updateToggleableTeamPerms(teamId: Identifier, payload: any) {
  let url = `/api/v1/console/teams/${teamId}/team-level-permissions/`
  return axios.post(url, payload).then((response) => {
    return response.data
  })
}

export async function getBrokerNoaAcknowledgments(teamId: Identifier) {
  return await axios.get(`/api/v1/console/teams/${teamId}/noa-acknowledgments/`)
}

export async function addOrUpdateBrokerNoaAcknowledgment(
  teamId: Identifier,
  brokerId: Identifier,
  zendeskUrl: String
) {
  const url = `/api/v1/console/teams/${teamId}/noa-acknowledgments/`
  const response = await axios.post(url, { broker_id: brokerId, zendesk_url: zendeskUrl })
  return response.status === HttpStatus.OK || response.status === HttpStatus.CREATED
}

export function fetchHomeFeed(userId: Identifier) {
  return axios.get(`/api/v1/console/users/${userId}/home-feed`).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

export async function getUserTimeline(userId: Identifier, filterOnlyStatusHistory: boolean) {
  return axios
    .get(`/api/v1/console/users/${userId}/timeline/?filter_status_history_only=${filterOnlyStatusHistory}`)
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        return response.data
      }
    })
}

export function completeChecklistItem({ userId, tasklistName }: { userId: string; tasklistName: string }) {
  return axios.post(`/api/v1/console/users/${userId}/complete-checklist-item/`, {
    tasklist_name: tasklistName,
  })
}
